import type { FC, ReactNode } from 'react'
import type { Fact } from '@/types'

import { motion } from 'motion/react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { factsIntro } from '@/constants/content'

interface FactsProps {
    facts: Array<Fact>
}

// The first two facts lead a row of their own, the rest follow three to a row.
const isLead = (index: number): boolean => index < 2
const startsRow = (index: number): boolean => (isLead(index) ? index === 0 : (index - 2) % 3 === 0)

// Facts as a calm editorial grid: big figures, short labels, thin rules. No pinned scrolling.
export const Facts: FC<FactsProps> = ({ facts = [] }): ReactNode => {
    if (!facts.length) return null

    return (
        <section id='fakte' data-tone='lilac' aria-labelledby='fakte-titulli' className='relative px-5 py-24 md:px-8 md:py-32 lg:px-12'>
            <div className='grid grid-cols-12 gap-x-5 gap-y-8'>
                <p className='col-span-12 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-tone-muted md:col-span-4'>
                    <span className='font-heading tabular-nums text-tone-accent'>04</span>
                    <span aria-hidden='true' className='h-px w-8 bg-tone-line' />
                    {factsIntro.label}
                </p>

                <div className='col-span-12 md:col-span-8'>
                    <h2
                        id='fakte-titulli'
                        className='font-heading text-5xl font-bold leading-[0.95] tracking-[-0.04em] [font-stretch:108%] sm:text-6xl lg:text-[5.4vw]'
                    >
                        {factsIntro.title}
                    </h2>

                    <p className='mt-5 max-w-xl font-serif text-2xl italic leading-snug text-tone-muted md:text-3xl'>
                        {factsIntro.text}
                    </p>
                </div>
            </div>

            <ol className='mt-14 grid grid-cols-12 border-t border-tone-line md:mt-20'>
                {facts.map((fact, index) => {
                    const lead = isLead(index)

                    return (
                        <motion.li
                            key={fact.id}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: lead ? index * 0.08 : ((index - 2) % 3) * 0.08 }}
                            className={cn(
                                'col-span-12 flex flex-col gap-4 border-b border-tone-line py-10 md:py-12 md:pr-8',
                                lead ? 'md:col-span-6' : 'md:col-span-4',
                                !startsRow(index) && 'md:border-l md:pl-8'
                            )}
                        >
                            <p
                                className={cn(
                                    'whitespace-nowrap font-heading font-black leading-[0.9] tracking-[-0.05em] text-gradient-deep [font-stretch:80%]',
                                    lead ? 'text-[24vw] md:text-[10vw]' : 'text-[19vw] md:text-[6.4vw]'
                                )}
                            >
                                <span data-mdlfy-cms={`faktet:${fact.id}:vlera`}>{fact.vlera}</span>
                            </p>

                            <h3 className='flex items-baseline gap-3 font-heading text-2xl font-bold tracking-[-0.02em] md:text-3xl'>
                                <span className='text-xs font-semibold tabular-nums text-tone-muted'>{pad(index + 1)}</span>
                                <span data-mdlfy-cms={`faktet:${fact.id}:njesia`}>{fact.njesia}</span>
                            </h3>

                            <p className='max-w-md text-base leading-relaxed text-tone-muted md:text-lg' data-mdlfy-cms={`faktet:${fact.id}:pershkrimi`}>
                                {fact.pershkrimi}
                            </p>
                        </motion.li>
                    )
                })}
            </ol>
        </section>
    )
}
